// components/EngineeringCapacityPanel.jsx
//
// Two exports:
//   default  → EngineeringCapacityPanel  (management UI — use as a tab in UsersTeams)
//   named    → EngineeringCapacityWallboard (read-only wallboard widget)
//
// ── Integration in UsersTeams.jsx ────────────────────────────────────────────
//
//   import EngineeringCapacityPanel, {
//     EngineeringCapacityWallboard,
//   } from "./EngineeringCapacityPanel";
//
//   1. Add "capacity" to activeTab state options.
//   2. Show the tab button for engineering teams (team.type === "engineering").
//   3. Render <EngineeringCapacityPanel> when activeTab === "capacity".
//
// ── API methods to add to api.js ─────────────────────────────────────────────
//
//   async getEngineerCapacity(teamId, month) {
//     return this.client.get(`/teams/${teamId}/engineer-capacity`, { params: { month } });
//   }
//   async saveEngineerCapacity(teamId, month, engineers) {
//     return this.client.put(`/teams/${teamId}/engineer-capacity`, { month, engineers });
//   }
//   async getEngineerCapacitySummary(teamId, month) {
//     return this.client.get(`/teams/${teamId}/engineer-capacity/summary`, { params: { month } });
//   }

import React, { useState, useEffect, useCallback } from "react";
import {
  Calendar,
  ChevronLeft,
  ChevronRight,
  Save,
  RefreshCw,
  AlertCircle,
  TrendingUp,
  Users,
  Wrench,
  PoundSterling,
  ToggleLeft,
  ToggleRight,
  Info,
  CheckCircle,
} from "lucide-react";
import apiService from "../services/api";
import { useTheme } from "../contexts/ThemeContext";

// ─── Utility ──────────────────────────────────────────────────────────────────

const fmt = {
  currency: (n) =>
    new Intl.NumberFormat("en-GB", {
      style: "currency",
      currency: "GBP",
      maximumFractionDigits: 0,
    }).format(n ?? 0),
  pct: (n) => (n == null ? "—" : `${n}%`),
  num: (n) => (n ?? 0).toLocaleString("en-GB"),
};

function monthLabel(monthYear) {
  const [y, m] = monthYear.split("-").map(Number);
  return new Date(y, m - 1, 1).toLocaleString("en-GB", {
    month: "long",
    year: "numeric",
  });
}

function offsetMonth(monthYear, delta) {
  const [y, m] = monthYear.split("-").map(Number);
  const d = new Date(y, m - 1 + delta, 1);
  return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, "0")}`;
}

function currentMonthYear() {
  const now = new Date();
  return `${now.getFullYear()}-${String(now.getMonth() + 1).padStart(2, "0")}`;
}

function utilisationColour(pct) {
  if (pct == null) return "text-gray-400";
  if (pct >= 90) return "text-green-400";
  if (pct >= 70) return "text-amber-400";
  return "text-red-400";
}

function utilisationBarColour(pct) {
  if (pct == null) return "bg-gray-600";
  if (pct >= 90) return "bg-green-500";
  if (pct >= 70) return "bg-amber-500";
  return "bg-red-500";
}

// ─── Small reusable pieces ────────────────────────────────────────────────────

const StatCard = ({ label, value, sub, icon: Icon, accent = "#427BCA" }) => (
  <div
    className="rounded-xl border p-5 flex flex-col gap-2"
    style={{ borderColor: `${accent}33`, backgroundColor: `${accent}0D` }}
  >
    <div className="flex items-center justify-between">
      <span className="text-xs font-medium uppercase tracking-wider opacity-60">
        {label}
      </span>
      {Icon && <Icon size={16} style={{ color: accent, opacity: 0.7 }} />}
    </div>
    <span className="text-2xl font-bold" style={{ color: accent }}>
      {value}
    </span>
    {sub && <span className="text-xs opacity-50">{sub}</span>}
  </div>
);

const NumInput = ({
  value,
  onChange,
  min = 0,
  max = 31,
  step = 0.5,
  disabled,
}) => (
  <input
    type="number"
    value={value}
    onChange={(e) => onChange(parseFloat(e.target.value) || 0)}
    min={min}
    max={max}
    step={step}
    disabled={disabled}
    className={`w-20 text-center border rounded px-2 py-1 text-sm
      bg-transparent focus:outline-none focus:border-[#427BCA] transition-colors
      ${disabled ? "opacity-30 cursor-not-allowed" : ""}
    `}
  />
);

// ─── Main management panel ────────────────────────────────────────────────────

const EngineeringCapacityPanel = ({ team, isManagement }) => {
  const { isDarkMode } = useTheme();

  const [monthYear, setMonthYear] = useState(currentMonthYear());
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState(null);
  const [saveSuccess, setSaveSuccess] = useState(false);

  // Data from API
  const [workingDayCount, setWorkingDayCount] = useState(0);
  const [bankHolidayCount, setBankHolidayCount] = useState(0);
  const [engineers, setEngineers] = useState([]); // { userId, user, role, capacity }

  // Local edits — keyed by userId
  const [edits, setEdits] = useState({});
  const [dirty, setDirty] = useState(false);

  // Styling (mirrors UsersTeams patterns)
  const cardBg = isDarkMode
    ? "bg-[#0C0E12] border-gray-700"
    : "bg-white border-gray-200";
  const inputCls = isDarkMode
    ? "bg-[#0A0B0F] border-gray-700 text-white focus:border-[#427BCA]"
    : "bg-white border-gray-300 text-gray-900 focus:border-[#427BCA]";
  const mutedText = isDarkMode ? "text-[#94979C]" : "text-gray-500";
  const theadBg = isDarkMode ? "bg-[#13161B]" : "bg-gray-50";
  const thText = isDarkMode ? "text-[#94979C]" : "text-gray-500";
  const rowBorder = isDarkMode
    ? "border-gray-700 hover:bg-gray-800/30"
    : "border-gray-200 hover:bg-gray-50";

  // ── Load ────────────────────────────────────────────────────────────────────

  const load = useCallback(async () => {
    if (!team?.id) return;
    setLoading(true);
    setError(null);
    try {
      const res = await apiService.getEngineerCapacity(team.id, monthYear);
      const data = res.data;
      setWorkingDayCount(data.workingDayCount ?? 0);
      setBankHolidayCount(data.bankHolidayCount ?? 0);
      setEngineers(data.engineers ?? []);
      // Seed edits from fetched data
      const initial = {};
      (data.engineers ?? []).forEach((e) => {
        initial[e.userId] = { ...e.capacity };
      });
      setEdits(initial);
      setDirty(false);
    } catch (err) {
      setError("Failed to load capacity data. Please try again.");
      console.error(err);
    } finally {
      setLoading(false);
    }
  }, [team?.id, monthYear]);

  useEffect(() => {
    load();
  }, [load]);

  // ── Edit helpers ─────────────────────────────────────────────────────────────

  const setField = (userId, field, value) => {
    setEdits((prev) => ({
      ...prev,
      [userId]: { ...prev[userId], [field]: value },
    }));
    setDirty(true);
    setSaveSuccess(false);
  };

  const toggleIncluded = (userId) => {
    setEdits((prev) => ({
      ...prev,
      [userId]: {
        ...prev[userId],
        includedInCapacity: !prev[userId]?.includedInCapacity,
      },
    }));
    setDirty(true);
    setSaveSuccess(false);
  };

  // ── Save ─────────────────────────────────────────────────────────────────────

  const handleSave = async () => {
    setSaving(true);
    setError(null);
    try {
      const payload = engineers.map((e) => ({
        userId: e.userId,
        ...edits[e.userId],
      }));
      await apiService.saveEngineerCapacity(team.id, monthYear, payload);
      setSaveSuccess(true);
      setDirty(false);
      setTimeout(() => setSaveSuccess(false), 3000);
    } catch (err) {
      setError("Failed to save. Please try again.");
      console.error(err);
    } finally {
      setSaving(false);
    }
  };

  // ── Derived totals ────────────────────────────────────────────────────────────

  const totals = engineers.reduce(
    (acc, e) => {
      const ed = edits[e.userId];
      if (!ed?.includedInCapacity) return acc;
      const absence =
        (ed.holidayDays ?? 0) + (ed.sickDays ?? 0) + (ed.otherAbsenceDays ?? 0);
      const available = Math.max(0, workingDayCount - absence);
      const slots = Math.round(available * (ed.slotsPerDay ?? 1));
      const potential = slots * (ed.avgInstallValue ?? 12449);
      acc.engineers++;
      acc.slots += slots;
      acc.potential += potential;
    },
    { engineers: 0, slots: 0, potential: 0 },
  ) ?? { engineers: 0, slots: 0, potential: 0 };

  // ── Month nav ────────────────────────────────────────────────────────────────

  const prevMonth = () => setMonthYear((m) => offsetMonth(m, -1));
  const nextMonth = () => setMonthYear((m) => offsetMonth(m, +1));
  const isCurrentMonth = monthYear === currentMonthYear();

  // ── Render ────────────────────────────────────────────────────────────────────

  return (
    <div className="space-y-6">
      {/* Header bar */}
      <div className={`rounded-xl border p-5 ${cardBg}`}>
        <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
          <div>
            <h2 className="text-lg font-semibold flex items-center gap-2">
              <Wrench size={20} className="text-[#427BCA]" />
              Install Capacity Planner
            </h2>
            <p className={`text-sm mt-0.5 ${mutedText}`}>
              Set each engineer's available slots for the month. Excludes
              appointments of type&nbsp;<em>Other</em> and UK bank holidays.
            </p>
          </div>

          {/* Month navigator */}
          <div className="flex items-center gap-2 shrink-0">
            <button
              onClick={prevMonth}
              className={`p-2 rounded-lg border transition-colors ${
                isDarkMode
                  ? "border-gray-700 hover:bg-gray-700"
                  : "border-gray-200 hover:bg-gray-100"
              }`}
            >
              <ChevronLeft size={16} />
            </button>
            <div
              className="flex items-center gap-2 px-4 py-2 rounded-lg border font-medium text-sm min-w-[160px] justify-center"
              style={{ borderColor: "#427BCA55", background: "#427BCA0D" }}
            >
              <Calendar size={14} className="text-[#427BCA]" />
              <span>{monthLabel(monthYear)}</span>
            </div>
            <button
              onClick={nextMonth}
              className={`p-2 rounded-lg border transition-colors ${
                isDarkMode
                  ? "border-gray-700 hover:bg-gray-700"
                  : "border-gray-200 hover:bg-gray-100"
              }`}
            >
              <ChevronRight size={16} />
            </button>
          </div>
        </div>

        {/* Month meta */}
        <div
          className={`flex flex-wrap gap-4 mt-4 pt-4 border-t text-sm ${isDarkMode ? "border-gray-700" : "border-gray-200"}`}
        >
          <span className={mutedText}>
            <span className="font-semibold text-white">{workingDayCount}</span>{" "}
            working days
          </span>
          <span className={mutedText}>
            <span className="font-semibold text-amber-400">
              {bankHolidayCount}
            </span>{" "}
            bank holiday{bankHolidayCount !== 1 ? "s" : ""}
          </span>
          {isCurrentMonth && (
            <span className="text-xs px-2 py-0.5 rounded-full bg-green-500/15 text-green-400 border border-green-500/30">
              Current month
            </span>
          )}
        </div>
      </div>

      {/* Summary KPI cards */}
      <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
        <StatCard
          label="Included Engineers"
          value={totals.engineers}
          sub={`of ${engineers.length} total`}
          icon={Users}
          accent="#427BCA"
        />
        <StatCard
          label="Available Install Slots"
          value={fmt.num(totals.slots)}
          sub={`after all absences`}
          icon={Wrench}
          accent="#8B5CF6"
        />
        <StatCard
          label="Potential Revenue"
          value={fmt.currency(totals.potential)}
          sub={`based on avg install values`}
          icon={PoundSterling}
          accent="#10B981"
        />
      </div>

      {/* Error banner */}
      {error && (
        <div className="flex items-center gap-3 px-4 py-3 rounded-xl bg-red-500/10 border border-red-500/30 text-red-400 text-sm">
          <AlertCircle size={16} className="shrink-0" />
          {error}
        </div>
      )}

      {/* Success banner */}
      {saveSuccess && (
        <div className="flex items-center gap-3 px-4 py-3 rounded-xl bg-green-500/10 border border-green-500/30 text-green-400 text-sm">
          <CheckCircle size={16} className="shrink-0" />
          Capacity saved successfully.
        </div>
      )}

      {/* Engineer table */}
      <div className={`rounded-xl border ${cardBg}`}>
        <div
          className={`p-5 border-b flex items-center justify-between ${isDarkMode ? "border-gray-700" : "border-gray-200"}`}
        >
          <div>
            <h3 className="font-semibold">Per-Engineer Capacity</h3>
            <p className={`text-xs mt-0.5 ${mutedText}`}>
              Toggle engineers in/out of the calculation and set their absences
              for this month.
            </p>
          </div>
          {isManagement && (
            <div className="flex items-center gap-3">
              <button
                onClick={load}
                disabled={loading}
                className={`p-2 rounded-lg border transition-colors ${
                  isDarkMode
                    ? "border-gray-700 hover:bg-gray-700"
                    : "border-gray-200 hover:bg-gray-100"
                }`}
                title="Refresh"
              >
                <RefreshCw
                  size={14}
                  className={loading ? "animate-spin" : ""}
                />
              </button>
              <button
                onClick={handleSave}
                disabled={!dirty || saving}
                className={`flex items-center gap-2 px-4 py-2 rounded-lg text-sm font-medium transition-all ${
                  dirty
                    ? "text-white"
                    : "opacity-40 cursor-not-allowed text-gray-400"
                }`}
                style={
                  dirty
                    ? {
                        background:
                          "radial-gradient(50% 50% at 50% 50%, #427BCA 0%, #2F578F 100%)",
                      }
                    : {}
                }
              >
                {saving ? (
                  <RefreshCw size={14} className="animate-spin" />
                ) : (
                  <Save size={14} />
                )}
                {saving ? "Saving…" : "Save Changes"}
              </button>
            </div>
          )}
        </div>

        {loading ? (
          <div className="py-16 text-center">
            <div className="w-10 h-10 border-4 border-[#427BCA] border-t-transparent rounded-full animate-spin mx-auto mb-3" />
            <p className={`text-sm ${mutedText}`}>Loading capacity data…</p>
          </div>
        ) : engineers.length === 0 ? (
          <div className="py-16 text-center">
            <Wrench
              size={40}
              className="mx-auto mb-3 text-[#427BCA] opacity-30"
            />
            <p className={`text-sm ${mutedText}`}>
              No active engineers in this team.
            </p>
          </div>
        ) : (
          <div className="overflow-x-auto">
            <table className="w-full">
              <thead className={theadBg}>
                <tr
                  className={`border-b ${isDarkMode ? "border-gray-700" : "border-gray-200"}`}
                >
                  {[
                    "Engineer",
                    "Included",
                    "Holiday Days",
                    "Sick Days",
                    "Other Absence",
                    "Slots/Day",
                    "Avg Install (£)",
                    "Available Slots",
                    "Potential Revenue",
                  ].map((h) => (
                    <th
                      key={h}
                      className={`text-left py-3 px-4 text-xs font-medium uppercase tracking-wider ${thText}`}
                    >
                      {h}
                    </th>
                  ))}
                  {isManagement && (
                    <th
                      className={`text-left py-3 px-4 text-xs font-medium uppercase tracking-wider ${thText}`}
                    >
                      Notes
                    </th>
                  )}
                </tr>
              </thead>
              <tbody>
                {engineers.map((eng) => {
                  const ed = edits[eng.userId] ?? eng.capacity;
                  const included = ed?.includedInCapacity ?? true;
                  const absence =
                    (ed?.holidayDays ?? 0) +
                    (ed?.sickDays ?? 0) +
                    (ed?.otherAbsenceDays ?? 0);
                  const availDays = Math.max(0, workingDayCount - absence);
                  const slots = Math.round(availDays * (ed?.slotsPerDay ?? 1));
                  const potential = slots * (ed?.avgInstallValue ?? 12449);
                  const name =
                    [eng.user?.firstName, eng.user?.lastName]
                      .filter(Boolean)
                      .join(" ") ||
                    eng.user?.email ||
                    "Unknown";
                  const initials = name
                    .split(" ")
                    .map((w) => w[0])
                    .join("")
                    .substring(0, 2)
                    .toUpperCase();

                  return (
                    <tr
                      key={eng.userId}
                      className={`border-b transition-colors ${rowBorder} ${
                        !included ? "opacity-40" : ""
                      }`}
                    >
                      {/* Engineer name */}
                      <td className="py-3 px-4">
                        <div className="flex items-center gap-3">
                          <div className="w-9 h-9 rounded-full bg-[#427BCA]/20 flex items-center justify-center shrink-0">
                            {eng.user?.avatar ? (
                              <img
                                src={eng.user.avatar}
                                alt={name}
                                className="w-full h-full rounded-full object-cover"
                              />
                            ) : (
                              <span className="text-xs font-semibold text-[#427BCA]">
                                {initials}
                              </span>
                            )}
                          </div>
                          <div>
                            <div className="font-medium text-sm">{name}</div>
                            <div className={`text-xs ${mutedText} capitalize`}>
                              {eng.role?.replace(/_/g, " ")}
                            </div>
                          </div>
                        </div>
                      </td>

                      {/* Included toggle */}
                      <td className="py-3 px-4">
                        {isManagement ? (
                          <button
                            onClick={() => toggleIncluded(eng.userId)}
                            className="transition-colors"
                            title={
                              included
                                ? "Click to exclude from capacity"
                                : "Click to include in capacity"
                            }
                          >
                            {included ? (
                              <ToggleRight
                                size={28}
                                className="text-green-400"
                              />
                            ) : (
                              <ToggleLeft size={28} className="text-gray-500" />
                            )}
                          </button>
                        ) : (
                          <span
                            className={`text-xs px-2 py-0.5 rounded-full border ${
                              included
                                ? "bg-green-500/15 text-green-400 border-green-500/30"
                                : "bg-gray-500/15 text-gray-400 border-gray-500/30"
                            }`}
                          >
                            {included ? "Yes" : "No"}
                          </span>
                        )}
                      </td>

                      {/* Absence fields — editable for managers */}
                      {["holidayDays", "sickDays", "otherAbsenceDays"].map(
                        (field) => (
                          <td key={field} className="py-3 px-4">
                            {isManagement ? (
                              <NumInput
                                value={ed?.[field] ?? 0}
                                onChange={(v) => setField(eng.userId, field, v)}
                                disabled={!included}
                                max={workingDayCount}
                              />
                            ) : (
                              <span className="text-sm">
                                {ed?.[field] ?? 0}
                              </span>
                            )}
                          </td>
                        ),
                      )}

                      {/* Slots per day */}
                      <td className="py-3 px-4">
                        {isManagement ? (
                          <NumInput
                            value={ed?.slotsPerDay ?? 1}
                            onChange={(v) =>
                              setField(eng.userId, "slotsPerDay", v)
                            }
                            min={0.5}
                            max={10}
                            step={0.5}
                            disabled={!included}
                          />
                        ) : (
                          <span className="text-sm">
                            {ed?.slotsPerDay ?? 1}
                          </span>
                        )}
                      </td>

                      {/* Avg install value */}
                      <td className="py-3 px-4">
                        {isManagement ? (
                          <input
                            type="number"
                            value={ed?.avgInstallValue ?? 12449}
                            onChange={(e) =>
                              setField(
                                eng.userId,
                                "avgInstallValue",
                                parseFloat(e.target.value) || 0,
                              )
                            }
                            disabled={!included}
                            min={0}
                            step={1}
                            className={`w-28 border rounded px-2 py-1 text-sm bg-transparent focus:outline-none focus:border-[#427BCA] ${
                              !included ? "opacity-30 cursor-not-allowed" : ""
                            }`}
                          />
                        ) : (
                          <span className="text-sm">
                            {fmt.currency(ed?.avgInstallValue ?? 12449)}
                          </span>
                        )}
                      </td>

                      {/* Computed outputs (read-only) */}
                      <td className="py-3 px-4 font-semibold text-sm text-[#427BCA]">
                        {included ? fmt.num(slots) : "—"}
                      </td>
                      <td className="py-3 px-4 font-semibold text-sm text-emerald-400">
                        {included ? fmt.currency(potential) : "—"}
                      </td>

                      {/* Notes */}
                      {isManagement && (
                        <td className="py-3 px-4">
                          <input
                            type="text"
                            value={ed?.notes ?? ""}
                            onChange={(e) =>
                              setField(eng.userId, "notes", e.target.value)
                            }
                            placeholder="Optional note…"
                            className={`w-40 border rounded px-2 py-1 text-xs bg-transparent focus:outline-none focus:border-[#427BCA] ${
                              isDarkMode
                                ? "border-gray-700 text-white placeholder-gray-600"
                                : "border-gray-200 text-gray-800 placeholder-gray-400"
                            }`}
                          />
                        </td>
                      )}
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        )}

        {/* Footer note */}
        <div
          className={`px-5 py-3 border-t flex items-start gap-2 text-xs ${mutedText} ${isDarkMode ? "border-gray-700" : "border-gray-200"}`}
        >
          <Info size={12} className="mt-0.5 shrink-0 text-[#427BCA]" />
          <span>
            Working days ({workingDayCount}) already exclude weekends and{" "}
            {bankHolidayCount} UK bank holiday
            {bankHolidayCount !== 1 ? "s" : ""} for {monthLabel(monthYear)}.
            Enter additional absences above. Available Slots = (Working Days −
            All Absences) × Slots/Day.
          </span>
        </div>
      </div>
    </div>
  );
};

// ─── Wallboard widget (read-only) ─────────────────────────────────────────────
//
// Usage on a wallboard page:
//   <EngineeringCapacityWallboard teamId={engineeringTeamId} />
//
// Polls every `refreshMs` milliseconds (default 5 min).

export const EngineeringCapacityWallboard = ({
  teamId,
  refreshMs = 5 * 60 * 1000,
}) => {
  const { isDarkMode } = useTheme();
  const [monthYear] = useState(currentMonthYear());
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [lastUpdated, setLastUpdated] = useState(null);

  const cardBg = isDarkMode
    ? "bg-[#0C0E12] border-gray-800"
    : "bg-white border-gray-200";
  const mutedText = isDarkMode ? "text-[#94979C]" : "text-gray-500";
  const rowBorder = isDarkMode ? "border-gray-800" : "border-gray-100";

  const fetchSummary = useCallback(async () => {
    try {
      const res = await apiService.getEngineerCapacitySummary(
        teamId,
        monthYear,
      );
      setData(res.data);
      setLastUpdated(new Date());
      setError(null);
    } catch (err) {
      setError("Unable to load data");
      console.error(err);
    } finally {
      setLoading(false);
    }
  }, [teamId, monthYear]);

  useEffect(() => {
    fetchSummary();
    const interval = setInterval(fetchSummary, refreshMs);
    return () => clearInterval(interval);
  }, [fetchSummary, refreshMs]);

  if (loading) {
    return (
      <div className={`rounded-2xl border p-8 text-center ${cardBg}`}>
        <div className="w-10 h-10 border-4 border-[#427BCA] border-t-transparent rounded-full animate-spin mx-auto mb-3" />
        <p className={`text-sm ${mutedText}`}>Loading installs capacity…</p>
      </div>
    );
  }

  if (error || !data) {
    return (
      <div className={`rounded-2xl border p-6 ${cardBg}`}>
        <div className="flex items-center gap-2 text-red-400 text-sm">
          <AlertCircle size={16} />
          {error ?? "No data"}
        </div>
      </div>
    );
  }

  const { totals, engineers, workingDayCount, bankHolidayCount } = data;
  const includedEngineers = (engineers ?? []).filter(
    (e) => e.includedInCapacity,
  );

  return (
    <div className={`rounded-2xl border overflow-hidden ${cardBg}`}>
      {/* Wallboard header */}
      <div
        className="px-6 py-4 flex items-center justify-between"
        style={{
          background:
            "radial-gradient(ellipse at top left, #427BCA22 0%, transparent 60%)",
          borderBottom: isDarkMode ? "1px solid #1f2937" : "1px solid #e5e7eb",
        }}
      >
        <div className="flex items-center gap-3">
          <div className="w-10 h-10 rounded-xl bg-[#427BCA]/20 flex items-center justify-center">
            <Wrench size={20} className="text-[#427BCA]" />
          </div>
          <div>
            <h3 className="font-bold text-lg leading-tight">
              Engineering Installs
            </h3>
            <p className={`text-xs ${mutedText}`}>
              {monthLabel(monthYear)} · {workingDayCount} working days ·{" "}
              {bankHolidayCount} bank holiday
              {bankHolidayCount !== 1 ? "s" : ""}
            </p>
          </div>
        </div>
        {lastUpdated && (
          <p className={`text-xs ${mutedText}`}>
            Updated{" "}
            {lastUpdated.toLocaleTimeString("en-GB", {
              hour: "2-digit",
              minute: "2-digit",
            })}
          </p>
        )}
      </div>

      {/* Top-level KPIs */}
      <div className="grid grid-cols-2 lg:grid-cols-4 gap-px bg-gray-800/20">
        {[
          {
            label: "Engineers",
            value: totals.engineerCount,
            icon: Users,
            accent: "#427BCA",
          },
          {
            label: "Available Slots",
            value: fmt.num(totals.totalAvailableSlots),
            icon: Wrench,
            accent: "#8B5CF6",
          },
          {
            label: "Potential Revenue",
            value: fmt.currency(totals.totalPotentialRevenue),
            icon: PoundSterling,
            accent: "#10B981",
          },
          {
            label: "Actual Revenue",
            value: fmt.currency(totals.totalActualRevenue),
            icon: TrendingUp,
            accent:
              totals.overallUtilisation >= 90
                ? "#10B981"
                : totals.overallUtilisation >= 70
                  ? "#F59E0B"
                  : "#EF4444",
          },
        ].map(({ label, value, icon: Icon, accent }) => (
          <div
            key={label}
            className={`px-5 py-4 ${isDarkMode ? "bg-[#0C0E12]" : "bg-white"}`}
          >
            <div className="flex items-center gap-2 mb-1">
              <Icon size={14} style={{ color: accent }} />
              <span
                className={`text-xs font-medium uppercase tracking-wider ${mutedText}`}
              >
                {label}
              </span>
            </div>
            <div className="text-2xl font-bold" style={{ color: accent }}>
              {value}
            </div>
          </div>
        ))}
      </div>

      {/* Utilisation bar */}
      {totals.overallUtilisation != null && (
        <div className="px-6 py-3 flex items-center gap-4">
          <span
            className={`text-xs font-medium uppercase tracking-wider ${mutedText} shrink-0`}
          >
            Team Utilisation
          </span>
          <div className="flex-1 h-2 rounded-full bg-gray-700/40 overflow-hidden">
            <div
              className={`h-full rounded-full transition-all duration-700 ${utilisationBarColour(totals.overallUtilisation)}`}
              style={{ width: `${Math.min(100, totals.overallUtilisation)}%` }}
            />
          </div>
          <span
            className={`text-sm font-bold shrink-0 ${utilisationColour(totals.overallUtilisation)}`}
          >
            {fmt.pct(totals.overallUtilisation)}
          </span>
        </div>
      )}

      {/* Per-engineer rows */}
      {includedEngineers.length > 0 && (
        <div
          className={`border-t ${isDarkMode ? "border-gray-800" : "border-gray-100"}`}
        >
          {includedEngineers.map((eng, idx) => {
            const name =
              [eng.user?.firstName, eng.user?.lastName]
                .filter(Boolean)
                .join(" ") || "Engineer";
            const initials = name
              .split(" ")
              .map((w) => w[0])
              .join("")
              .substring(0, 2)
              .toUpperCase();

            return (
              <div
                key={eng.userId}
                className={`px-6 py-3 flex items-center gap-4 border-b ${rowBorder} ${
                  idx % 2 === 0
                    ? isDarkMode
                      ? "bg-white/[0.02]"
                      : "bg-gray-50/60"
                    : ""
                }`}
              >
                {/* Avatar */}
                <div className="w-8 h-8 rounded-full bg-[#427BCA]/20 flex items-center justify-center shrink-0">
                  <span className="text-xs font-semibold text-[#427BCA]">
                    {initials}
                  </span>
                </div>

                {/* Name */}
                <span className="font-medium text-sm w-36 truncate shrink-0">
                  {name}
                </span>

                {/* Progress bar */}
                <div className="flex-1 flex items-center gap-3">
                  <div className="flex-1 h-1.5 rounded-full bg-gray-700/30 overflow-hidden">
                    <div
                      className={`h-full rounded-full ${utilisationBarColour(eng.capacityUtilisation)}`}
                      style={{
                        width: `${Math.min(
                          100,
                          eng.capacityUtilisation ?? 0,
                        )}%`,
                      }}
                    />
                  </div>
                </div>

                {/* Stats */}
                <div className="flex items-center gap-6 text-sm shrink-0">
                  <div className="text-right">
                    <div className="font-semibold">
                      {eng.completedInstalls}/{eng.availableSlots}
                    </div>
                    <div className={`text-xs ${mutedText}`}>installs/slots</div>
                  </div>
                  <div className="text-right">
                    <div
                      className={`font-semibold ${utilisationColour(eng.capacityUtilisation)}`}
                    >
                      {fmt.pct(eng.capacityUtilisation)}
                    </div>
                    <div className={`text-xs ${mutedText}`}>utilisation</div>
                  </div>
                  <div className="text-right">
                    <div className="font-semibold text-emerald-400">
                      {fmt.currency(eng.actualRevenue)}
                    </div>
                    <div className={`text-xs ${mutedText}`}>actual</div>
                  </div>
                  <div className="text-right">
                    <div className={`font-semibold ${mutedText}`}>
                      {fmt.currency(eng.potentialRevenue)}
                    </div>
                    <div className={`text-xs ${mutedText}`}>potential</div>
                  </div>
                </div>
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
};

export default EngineeringCapacityPanel;
